import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Slider } from "../../components/ui/slider";
import { ArrowLeft, Download, Trash2, RotateCw, Upload, ZoomIn, ZoomOut, Move, RotateCcw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export default function TemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [template, setTemplate] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [draggedImage, setDraggedImage] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 850, height: 1100 });
  const [prefixImage, setPrefixImage] = useState(null);
  
  // Image adjustment state (pan, zoom, rotate within slot)
  const [imageAdjustments, setImageAdjustments] = useState({});
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [adjustmentMode, setAdjustmentMode] = useState(null); // 'pan', 'zoom', 'rotate'
  
  // Image drag adjustment state (for touch/mouse dragging within slot)
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [dragStartOffset, setDragStartOffset] = useState({ x: 0, y: 0 });
  
  // Text-postcard drag state
  const [textPositions, setTextPositions] = useState({
    from: { x: 30, y: 40 },
    to: { x: 30, y: 175 },
    bottom: { x: 30, y: 470 }
  });
  const [draggedText, setDraggedText] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Adjust canvas size for single photo and postcard templates
    if (template?.template_type === "single") {
      setCanvasSize({ width: 400, height: 600 }); // 4" x 6" portrait at 100 pixels per inch
    } else if (template?.template_type === "postcard" || template?.template_type === "text-postcard") {
      setCanvasSize({ width: 350, height: 650 }); // 3.5" x 6.5" portrait at 100 pixels per inch
    } else {
      setCanvasSize({ width: 850, height: 1100 }); // 8.5" x 11"
    }
  }, [template?.template_type]);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [uploadingToSlot, setUploadingToSlot] = useState(null);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [applyingBulk, setApplyingBulk] = useState(false);
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  const bulkMultiFileInputRef = useRef(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [bulkMultiUploading, setBulkMultiUploading] = useState(false);
  const [bulkMultiProgress, setBulkMultiProgress] = useState({ current: 0, total: 0 });

  const SCALE = 100; // 100 pixels = 1 inch

  useEffect(() => {
    fetchTemplate();
    fetchImages();
  }, [templateId]);

  useEffect(() => {
    if (template) {
      drawCanvas();
    }
  }, [template, images, selectedImage, hoveredSlot, loadedImages, prefixImage, imageAdjustments]);

  // Track loading images to prevent duplicate loads
  const loadingImagesRef = useRef(new Set());

  useEffect(() => {
    // Load images with proper error handling and cache busting
    const loadImages = () => {
      const imageIds = images.map(img => img.id);
      
      // Clean up loadedImages that are no longer in the images array
      setLoadedImages(prev => {
        const newCache = {};
        for (const [id, img] of Object.entries(prev)) {
          if (imageIds.includes(id)) {
            newCache[id] = img;
          }
        }
        // Also clean up loading ref
        loadingImagesRef.current = new Set(
          [...loadingImagesRef.current].filter(id => imageIds.includes(id))
        );
        return newCache;
      });
      
      // Load new images
      for (const img of images) {
        // Skip if already loading or loaded
        if (loadingImagesRef.current.has(img.id)) continue;
        
        // Mark as loading
        loadingImagesRef.current.add(img.id);
        
        const image = new Image();
        image.crossOrigin = "anonymous";
        
        // Add cache-busting timestamp to prevent stale images
        const timestamp = Date.now();
        const imageUrl = `${API}/images/${img.id}?t=${timestamp}`;
        
        image.onload = () => {
          setLoadedImages(prev => {
            // Only add if not already present (avoid overwriting with different instance)
            if (prev[img.id] && prev[img.id] !== null) return prev;
            return { ...prev, [img.id]: image };
          });
        };
        
        image.onerror = (e) => {
          console.error(`Failed to load image ${img.id}:`, e);
          // Still add to cache with null to indicate error
          setLoadedImages(prev => {
            if (prev[img.id] !== undefined) return prev;
            return { ...prev, [img.id]: null };
          });
        };
        
        image.src = imageUrl;
      }
    };
    
    loadImages();
  }, [images]);

  // Load image adjustments when images change
  useEffect(() => {
    const adjustments = {};
    images.forEach(img => {
      adjustments[img.id] = {
        offset_x: img.offset_x || 0,
        offset_y: img.offset_y || 0,
        scale: img.scale || 1,
        display_rotation: img.display_rotation || 0
      };
    });
    setImageAdjustments(adjustments);
  }, [images]);

  useEffect(() => {
    // Load prefix image for branded templates
    if (template?.template_type === "branded" && template?.prefix_image_url) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setPrefixImage(img);
      };
      img.onerror = () => {
        console.error("Failed to load prefix image");
      };
      // Use API endpoint to serve the prefix image
      img.src = `${API}/prefix-images/${templateId}`;
    }
  }, [template?.prefix_image_url, template?.template_type, templateId]);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}`);
      setTemplate(response.data);
    } catch (error) {
      alert("Failed to load template");
      navigate("/");
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}/images`);
      setImages(response.data);
    } catch (error) {
      alert("Failed to load images");
    }
  };

  const getShapePositions = () => {
    if (!template) return [];
    
    if (template.template_type === "single") {
      // Single photo fills entire 4" x 6" portrait page
      const rectWidth = 4 * SCALE;
      const rectHeight = 6 * SCALE;
      
      return [{
        index: 0,
        x: 0,
        y: 0,
        width: rectWidth,
        height: rectHeight,
        type: "rectangle"
      }];
    } else if (template.template_type === "branded") {
      // Branded card: 2.25" x 3.25" rectangles in a grid on A4/Letter paper
      const rectWidth = 2.25 * SCALE;
      const rectHeight = 3.25 * SCALE;
      const pageWidth = 8.5 * SCALE;
      const pageHeight = 11 * SCALE;
      const margin = 0.25 * SCALE;
      
      // Calculate how many cards fit (3 columns x 3 rows = 9 per page)
      const cols = 3;
      const rows = 3;
      const spacingX = (pageWidth - cols * rectWidth - 2 * margin) / (cols - 1);
      const spacingY = (pageHeight - rows * rectHeight - 2 * margin) / (rows - 1);
      
      const positions = [];
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          positions.push({
            index: row * cols + col,
            x: margin + col * (rectWidth + spacingX),
            y: margin + row * (rectHeight + spacingY),
            width: rectWidth,
            height: rectHeight,
            type: "rectangle"
          });
        }
      }
      return positions;
    } else if (template.template_type === "postcard") {
      // Postcard fills entire 3.5" x 6.5" portrait page
      const rectWidth = 3.5 * SCALE;
      const rectHeight = 6.5 * SCALE;
      
      return [{
        index: 0,
        x: 0,
        y: 0,
        width: rectWidth,
        height: rectHeight,
        type: "rectangle"
      }];
    } else if (template.template_type === "text-postcard") {
      // Text postcard - plain white rectangle, no image slots
      const rectWidth = 3.5 * SCALE;
      const rectHeight = 6.5 * SCALE;
      
      return [{
        index: 0,
        x: 0,
        y: 0,
        width: rectWidth,
        height: rectHeight,
        type: "text-only"
      }];
    } else if (template.template_type === "rectangle") {
      // 4" x 6" rectangles, 2x2 grid
      const rectWidth = 3.3 * SCALE;
      const rectHeight = 5.5 * SCALE;
      const totalWidth = 2 * rectWidth;
      const totalHeight = 2 * rectHeight;
      const marginX = (8.5 * SCALE - totalWidth) / 2;
      const marginY = (11 * SCALE - totalHeight) / 2;
      
      const positions = [];
      for (let row = 0; row < 2; row++) {
        for (let col = 0; col < 2; col++) {
          positions.push({
            index: row * 2 + col,
            x: marginX + col * rectWidth,
            y: marginY + row * rectHeight,
            width: rectWidth,
            height: rectHeight,
            type: "rectangle"
          });
        }
      }
      return positions;
    } else {
      // Circles: 1.97" for standard, 1.5" for small
      const isSmallCircle = template?.template_type === 'circle-small';
      const circleDiameter = (isSmallCircle ? 1.5 : 1.97) * SCALE;
      const margin = 0.4 * SCALE;
      const availableWidth = 8.5 * SCALE - 2 * margin;
      const availableHeight = 11 * SCALE - 2 * margin;
      
      const cols = Math.floor(availableWidth / circleDiameter);
      const rows = Math.floor(availableHeight / circleDiameter);
      
      const spacingX = (availableWidth - cols * circleDiameter) / (cols + 1);
      const spacingY = (availableHeight - rows * circleDiameter) / (rows + 1);
      
      const positions = [];
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          positions.push({
            index: row * cols + col,
            x: margin + spacingX + col * (circleDiameter + spacingX),
            y: margin + spacingY + row * (circleDiameter + spacingY),
            width: circleDiameter,
            height: circleDiameter,
            type: "circle"
          });
        }
      }
      return positions;
    }
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw page background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw shapes (no borders for single photo templates)
    const positions = getShapePositions();
    
    positions.forEach((pos) => {
      // Only draw borders for non-single, non-postcard, and non-text-postcard templates
      if (template.template_type !== "single" && template.template_type !== "postcard" && template.template_type !== "text-postcard") {
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = 2;
        
        if (pos.type === "rectangle") {
          ctx.strokeRect(pos.x, pos.y, pos.width, pos.height);
        } else {
          const centerX = pos.x + pos.width / 2;
          const centerY = pos.y + pos.height / 2;
          const radius = pos.width / 2;
          
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          ctx.stroke();
        }

        // Draw slot number
        ctx.fillStyle = "#666666";
        ctx.font = "12px Inter";
        ctx.textAlign = "center";
        ctx.fillText(`#${pos.index + 1}`, pos.x + pos.width / 2, pos.y + 20);
      }
    });

    // Draw slots and images
    positions.forEach((pos) => {
      const img = images.find(i => i.circle_index === pos.index);
      const isHovered = hoveredSlot === pos.index;
      
      if (img) {
        // Has image - draw it
        const loadedImg = loadedImages[img.id];
        
        if (loadedImg) {
          ctx.save();
          
          // For branded template, draw in 5/6 top area only (changed from 3/4)
          const isBranded = template?.template_type === "branded";
          const userAreaHeight = isBranded ? pos.height * (5/6) : pos.height;
          
          // Create clipping path for user image area
          ctx.beginPath();
          if (pos.type === "rectangle") {
            ctx.rect(pos.x, pos.y, pos.width, userAreaHeight);
          } else {
            ctx.arc(pos.x + pos.width / 2, pos.y + pos.height / 2, pos.width / 2, 0, 2 * Math.PI);
          }
          ctx.clip();
          
          // Get image adjustments
          const adjustment = imageAdjustments[img.id] || { offset_x: 0, offset_y: 0, scale: 1, display_rotation: 0 };
          
          // Calculate base scale to FIT the image (shows full image without cropping)
          const baseScale = Math.min(pos.width / loadedImg.width, userAreaHeight / loadedImg.height);
          // Apply user's zoom adjustment (1.0 = fit, >1.0 = zoom in to crop)
          const finalScale = baseScale * adjustment.scale;
          
          const imgWidth = loadedImg.width * finalScale;
          const imgHeight = loadedImg.height * finalScale;
          
          // Calculate base position (centered)
          let imgX = pos.x + (pos.width - imgWidth) / 2;
          let imgY = pos.y + (userAreaHeight - imgHeight) / 2;
          
          // Apply pan offset (percentage of slot size)
          imgX += (adjustment.offset_x / 100) * pos.width;
          imgY += (adjustment.offset_y / 100) * userAreaHeight;
          
          // Apply display rotation if set
          if (adjustment.display_rotation !== 0) {
            const centerX = pos.x + pos.width / 2;
            const centerY = pos.y + userAreaHeight / 2;
            ctx.translate(centerX, centerY);
            ctx.rotate((adjustment.display_rotation * Math.PI) / 180);
            ctx.translate(-centerX, -centerY);
          }
          
          ctx.drawImage(loadedImg, imgX, imgY, imgWidth, imgHeight);
          ctx.restore();
          
          // Draw prefix image in bottom 1/6 for branded templates
          if (isBranded && prefixImage) {
            ctx.save();
            const prefixAreaY = pos.y + userAreaHeight;
            const prefixAreaHeight = pos.height - userAreaHeight;
            
            ctx.beginPath();
            ctx.rect(pos.x, prefixAreaY, pos.width, prefixAreaHeight);
            ctx.clip();
            
            // Scale prefix to fit
            const prefixScale = Math.max(pos.width / prefixImage.width, prefixAreaHeight / prefixImage.height);
            const prefixWidth = prefixImage.width * prefixScale;
            const prefixHeight = prefixImage.height * prefixScale;
            const prefixX = pos.x + (pos.width - prefixWidth) / 2;
            const prefixY = prefixAreaY + (prefixAreaHeight - prefixHeight) / 2;
            
            ctx.drawImage(prefixImage, prefixX, prefixY, prefixWidth, prefixHeight);
            ctx.restore();
          } else if (isBranded) {
            // Show placeholder for prefix area
            ctx.fillStyle = "#f3e8ff";
            ctx.fillRect(pos.x, pos.y + userAreaHeight, pos.width, pos.height - userAreaHeight);
            ctx.fillStyle = "#9333ea";
            ctx.font = "10px Inter";
            ctx.textAlign = "center";
            ctx.fillText("Logo Area", pos.x + pos.width / 2, pos.y + userAreaHeight + (pos.height - userAreaHeight) / 2 + 4);
          }
        } else {
          // Loading or error placeholder
          const isError = loadedImages[img.id] === null;
          ctx.fillStyle = isError ? "#fee2e2" : "#e5e7eb";
          if (pos.type === "rectangle") {
            ctx.fillRect(pos.x, pos.y, pos.width, pos.height);
          } else {
            ctx.beginPath();
            ctx.arc(pos.x + pos.width / 2, pos.y + pos.height / 2, pos.width / 2, 0, 2 * Math.PI);
            ctx.fill();
          }
          
          ctx.fillStyle = isError ? "#dc2626" : "#9ca3af";
          ctx.font = "12px Inter";
          ctx.textAlign = "center";
          ctx.fillText(isError ? "Load Error" : "Loading...", pos.x + pos.width / 2, pos.y + pos.height / 2);
          
          // Add retry hint for errors
          if (isError) {
            ctx.font = "10px Inter";
            ctx.fillText("Click to retry", pos.x + pos.width / 2, pos.y + pos.height / 2 + 15);
          }
        }
        
        // Highlight if selected
        if (selectedImage && selectedImage.id === img.id) {
          ctx.strokeStyle = "#8b5cf6";
          ctx.lineWidth = 4;
          if (pos.type === "rectangle") {
            ctx.strokeRect(pos.x, pos.y, pos.width, pos.height);
          } else {
            ctx.beginPath();
            ctx.arc(pos.x + pos.width / 2, pos.y + pos.height / 2, pos.width / 2, 0, 2 * Math.PI);
            ctx.stroke();
          }
          
          // Show drag hint
          ctx.fillStyle = "rgba(139, 92, 246, 0.9)";
          ctx.font = "bold 12px Inter";
          ctx.textAlign = "center";
          ctx.fillText("Drag to adjust position", pos.x + pos.width / 2, pos.y + pos.height - 10);
        }
      } else {
        // Empty slot
        // For text-only postcards, don't show add image buttons
        if (pos.type !== "text-only") {
          const isBranded = template?.template_type === "branded";
          const userAreaHeight = isBranded ? pos.height * (5/6) : pos.height;
          
          // Show + button on hover in user area
          if (isHovered) {
            ctx.fillStyle = "rgba(139, 92, 246, 0.1)";
            if (pos.type === "rectangle") {
              ctx.fillRect(pos.x, pos.y, pos.width, userAreaHeight);
            } else {
              ctx.beginPath();
              ctx.arc(pos.x + pos.width / 2, pos.y + pos.height / 2, pos.width / 2, 0, 2 * Math.PI);
              ctx.fill();
            }
          }
          
          // Draw + icon in user area
          const centerX = pos.x + pos.width / 2;
          const centerY = pos.y + userAreaHeight / 2;
          const plusSize = isHovered ? 30 : 20;
          
          ctx.strokeStyle = isHovered ? "#8b5cf6" : "#d1d5db";
          ctx.lineWidth = isHovered ? 4 : 3;
          ctx.lineCap = "round";
          
          // Vertical line
          ctx.beginPath();
          ctx.moveTo(centerX, centerY - plusSize / 2);
          ctx.lineTo(centerX, centerY + plusSize / 2);
          ctx.stroke();
          
          // Horizontal line
          ctx.beginPath();
          ctx.moveTo(centerX - plusSize / 2, centerY);
          ctx.lineTo(centerX + plusSize / 2, centerY);
          ctx.stroke();
          
          // "Add Image" text on hover
          if (isHovered) {
            ctx.fillStyle = "#8b5cf6";
            ctx.font = "bold 12px Inter";
            ctx.textAlign = "center";
            ctx.fillText("Add Image", centerX, centerY + plusSize + 15);
          }
          
          // Show prefix area for branded templates (empty slot)
          if (isBranded) {
            const prefixAreaY = pos.y + userAreaHeight;
            const prefixAreaHeight = pos.height - userAreaHeight;
            
            if (prefixImage) {
              ctx.save();
              ctx.beginPath();
              ctx.rect(pos.x, prefixAreaY, pos.width, prefixAreaHeight);
              ctx.clip();
              
              const prefixScale = Math.max(pos.width / prefixImage.width, prefixAreaHeight / prefixImage.height);
              const prefixWidth = prefixImage.width * prefixScale;
              const prefixHeight = prefixImage.height * prefixScale;
              const prefixX = pos.x + (pos.width - prefixWidth) / 2;
              const prefixY = prefixAreaY + (prefixAreaHeight - prefixHeight) / 2;
              
              ctx.drawImage(prefixImage, prefixX, prefixY, prefixWidth, prefixHeight);
              ctx.restore();
            } else {
              // Placeholder for prefix area
              ctx.fillStyle = "#f3e8ff";
              ctx.fillRect(pos.x, prefixAreaY, pos.width, prefixAreaHeight);
              ctx.fillStyle = "#9333ea";
              ctx.font = "10px Inter";
              ctx.textAlign = "center";
              ctx.fillText("Logo Area", pos.x + pos.width / 2, prefixAreaY + prefixAreaHeight / 2 + 4);
            }
          }
        } else {
          // Text-only postcard - ALL text on LEFT side, right side empty
          ctx.save();
          ctx.fillStyle = "#000000";
          
          // Rotate canvas 90 degrees for vertical text
          ctx.translate(pos.x + pos.width / 2, pos.y + pos.height / 2);
          ctx.rotate(-Math.PI / 2);
          ctx.translate(-(pos.y + pos.height / 2), -(pos.x + pos.width / 2));
          
          // Now drawing in rotated context (height becomes width, width becomes height)
          const canvasHeight = pos.width;
          const canvasWidth = pos.height;
          
          ctx.textAlign = "left";
          
          // From section (top left)
          const leftX = pos.y + 30;
          ctx.font = "10px Inter";
          let yPos = pos.x + 40;
          ctx.fillText("From", leftX, yPos);
          yPos += 15;
          
          const fromAddr = template?.from_address || "POTRAIT CHOCOLATE, Hawi, HI 96719";
          const fromLines = fromAddr.split(',');
          fromLines.forEach(line => {
            ctx.fillText(line.trim(), leftX, yPos);
            yPos += 12;
          });
          
          // To section (TRUE middle - centered horizontally and vertically)
          const middleX = pos.y + canvasWidth / 2 - 40; // More centered
          yPos = pos.x + canvasHeight / 2 - 20; // Higher up to avoid overlap
          ctx.fillText("TO", middleX, yPos);
          yPos += 15;
          
          const toAddr = template?.to_address || "Name, Address";
          const toLines = toAddr.split(',');
          toLines.forEach(line => {
            ctx.fillText(line.trim(), middleX, yPos);
            yPos += 12;
          });
          
          // Bottom section (ingredients, nutrition) - Further down to avoid overlap
          yPos = pos.x + canvasHeight - 140; // Moved up from 180
          
          // Ingredients (removed manufacturing line)
          const ingredients = template?.ingredients || "";
          if (ingredients) {
            ctx.font = "bold 9px Inter";
            ctx.fillText("Ingredients:", leftX, yPos);
            yPos += 12;
            ctx.font = "8px Inter";
            
            // Word wrap for ingredients
            const words = ingredients.split(' ');
            let line = "";
            const maxWidth = 150;
            words.forEach(word => {
              const testLine = line + word + " ";
              const metrics = ctx.measureText(testLine);
              if (metrics.width > maxWidth && line !== "") {
                ctx.fillText(line, leftX, yPos);
                line = word + " ";
                yPos += 10;
              } else {
                line = testLine;
              }
            });
            if (line) {
              ctx.fillText(line, leftX, yPos);
              yPos += 12;
            }
          }
          
          // Nutrition Facts
          const nutrition = template?.nutrition_facts || "";
          if (nutrition) {
            ctx.font = "bold 9px Inter";
            ctx.fillText("Nutrition Facts:", leftX, yPos);
            yPos += 12;
            ctx.font = "8px Inter";
            
            // Word wrap nutrition
            const nutritionWords = nutrition.split(' ');
            let nutritionLine = "";
            nutritionWords.forEach(word => {
              const testLine = nutritionLine + word + " ";
              const metrics = ctx.measureText(testLine);
              if (metrics.width > 150 && nutritionLine !== "") {
                ctx.fillText(nutritionLine, leftX, yPos);
                nutritionLine = word + " ";
                yPos += 10;
              } else {
                nutritionLine = testLine;
              }
            });
            if (nutritionLine) {
              ctx.fillText(nutritionLine, leftX, yPos);
            }
          }
          
          // RIGHT SIDE - Completely empty (no text)
          
          ctx.restore();
        }
      }
    });
  };

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Handle both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
    
    if (!clientX || !clientY) return;
    
    // Scale coordinates for canvas size vs display size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    const positions = getShapePositions();
    
    // Check if clicked on an image or slot
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const img = images.find(img => img.circle_index === i);
      
      let isInside = false;
      
      if (pos.type === "rectangle") {
        isInside = x >= pos.x && x <= pos.x + pos.width && y >= pos.y && y <= pos.y + pos.height;
      } else {
        const centerX = pos.x + pos.width / 2;
        const centerY = pos.y + pos.height / 2;
        const radius = pos.width / 2;
        const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
        isInside = distance <= radius;
      }
      
      if (isInside) {
        if (img) {
          // Check if image failed to load - retry on click
          if (loadedImages[img.id] === null) {
            // Remove from cache and loading ref to trigger reload
            loadingImagesRef.current.delete(img.id);
            setLoadedImages(prev => {
              const newCache = { ...prev };
              delete newCache[img.id];
              return newCache;
            });
            // Trigger re-fetch by toggling images state
            fetchImages();
            return;
          }
          setSelectedImage(img);
        } else {
          // Empty slot - trigger upload
          setUploadingToSlot(i);
          fileInputRef.current?.click();
        }
        return;
      }
    }

    // Clicked on empty space
    setSelectedImage(null);
  };

  const handleCanvasMouseMove = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const positions = getShapePositions();
    
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      let isInside = false;
      
      if (pos.type === "rectangle") {
        isInside = x >= pos.x && x <= pos.x + pos.width && y >= pos.y && y <= pos.y + pos.height;
      } else {
        const centerX = pos.x + pos.width / 2;
        const centerY = pos.y + pos.height / 2;
        const radius = pos.width / 2;
        const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
        isInside = distance <= radius;
      }
      
      if (isInside) {
        setHoveredSlot(i);
        return;
      }
    }
    
    setHoveredSlot(null);
  };

  // Handle drag start for image position adjustment (mouse and touch)
  const handleDragStart = (e) => {
    if (!selectedImage) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
    
    if (!clientX || !clientY) return;
    
    // Check if we're clicking on the selected image's slot
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    const positions = getShapePositions();
    const pos = positions.find((_, i) => images.find(img => img.circle_index === i)?.id === selectedImage.id);
    
    if (pos) {
      const isInside = pos.type === "rectangle" 
        ? (x >= pos.x && x <= pos.x + pos.width && y >= pos.y && y <= pos.y + pos.height)
        : Math.sqrt((x - (pos.x + pos.width/2))**2 + (y - (pos.y + pos.height/2))**2) <= pos.width/2;
      
      if (isInside) {
        setIsDraggingImage(true);
        setDragStartPos({ x: clientX, y: clientY });
        setDragStartOffset({
          x: imageAdjustments[selectedImage.id]?.offset_x || 0,
          y: imageAdjustments[selectedImage.id]?.offset_y || 0
        });
      }
    }
  };

  const handleDragMove = (e) => {
    if (!isDraggingImage || !selectedImage) return;
    
    e.preventDefault();
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
    
    if (!clientX || !clientY) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate movement in canvas coordinates
    const deltaX = (clientX - dragStartPos.x) / rect.width * 100;
    const deltaY = (clientY - dragStartPos.y) / rect.height * 100;
    
    // Update the offset (clamped to -100 to 100)
    const newOffsetX = Math.max(-100, Math.min(100, dragStartOffset.x + deltaX * 2));
    const newOffsetY = Math.max(-100, Math.min(100, dragStartOffset.y + deltaY * 2));
    
    setImageAdjustments(prev => ({
      ...prev,
      [selectedImage.id]: {
        ...prev[selectedImage.id],
        offset_x: newOffsetX,
        offset_y: newOffsetY
      }
    }));
  };

  const handleDragEnd = () => {
    setIsDraggingImage(false);
  };

  const deleteImage = async () => {
    if (!selectedImage) return;

    if (!window.confirm("Delete this image?")) return;

    try {
      await axios.delete(`${API}/images/${selectedImage.id}`);
      alert("Image deleted");
      
      // Clear from loaded images cache
      setLoadedImages(prev => {
        const newCache = { ...prev };
        delete newCache[selectedImage.id];
        return newCache;
      });
      
      setSelectedImage(null);
      fetchTemplate();
      fetchImages();
    } catch (error) {
      alert("Failed to delete image");
    }
  };

  const rotateImage = async (angle) => {
    if (!selectedImage) return;

    try {
      await axios.post(`${API}/images/${selectedImage.id}/rotate?angle=${angle}`);
      alert(`Image rotated to ${angle}°`);
      
      // Clear loaded image cache and loading ref to force reload
      loadingImagesRef.current.delete(selectedImage.id);
      setLoadedImages(prev => {
        const newCache = { ...prev };
        delete newCache[selectedImage.id];
        return newCache;
      });
      
      // Refresh images
      fetchImages();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to rotate image");
    }
  };

  // Image adjustment functions (pan, zoom, rotate within slot)
  const updateImageAdjustment = (field, value) => {
    if (!selectedImage) return;
    
    setImageAdjustments(prev => ({
      ...prev,
      [selectedImage.id]: {
        ...prev[selectedImage.id],
        [field]: value
      }
    }));
  };

  // Auto-save adjustments when they change (debounced)
  const saveTimeoutRef = useRef(null);
  
  useEffect(() => {
    if (!selectedImage) return;
    const adjustment = imageAdjustments[selectedImage.id];
    if (!adjustment) return;
    
    // Clear previous timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Auto-save after 500ms of no changes
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/images/${selectedImage.id}/adjust`, adjustment);
        console.log("Auto-saved adjustment for", selectedImage.id);
      } catch (error) {
        console.error("Failed to auto-save adjustment:", error);
      }
    }, 500);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [imageAdjustments, selectedImage]);

  const saveImageAdjustment = async () => {
    if (!selectedImage) return;
    
    const adjustment = imageAdjustments[selectedImage.id];
    if (!adjustment) return;
    
    try {
      await axios.put(`${API}/images/${selectedImage.id}/adjust`, adjustment);
      alert("Image adjustment saved!");
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to save adjustment");
    }
  };

  const resetImageAdjustment = () => {
    if (!selectedImage) return;
    
    setImageAdjustments(prev => ({
      ...prev,
      [selectedImage.id]: {
        offset_x: 0,
        offset_y: 0,
        scale: 1,
        display_rotation: 0
      }
    }));
  };

  const rotateDisplayImage = (direction) => {
    if (!selectedImage) return;
    
    const current = imageAdjustments[selectedImage.id]?.display_rotation || 0;
    const newRotation = direction === 'cw' 
      ? (current + 90) % 360 
      : (current - 90 + 360) % 360;
    
    updateImageAdjustment('display_rotation', newRotation);
  };

  const moveImageToSlot = async (newIndex) => {
    if (!selectedImage) return;

    try {
      await axios.put(`${API}/templates/${templateId}/move-image`, {
        image_id: selectedImage.id,
        new_circle_index: newIndex,
      });
      alert("Image moved");
      fetchImages();
    } catch (error) {
      alert("Failed to move image");
    }
  };

  // Download individual image as PDF with sequence number
  const downloadImagePdf = async (imageId, sequenceNumber) => {
    try {
      // Auto-save any pending adjustments before downloading
      const adjustment = imageAdjustments[imageId];
      if (adjustment) {
        await axios.put(`${API}/images/${imageId}/adjust`, adjustment);
      }
      
      const response = await axios.get(`${API}/images/${imageId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sequenceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download PDF:", error);
      alert("Failed to download PDF");
    }
  };

  const uploadImageToSlot = async (file, slotIndex) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/templates/${templateId}/upload-owner`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      // Move to specific slot if needed
      if (slotIndex !== null && slotIndex !== undefined) {
        const imageId = response.data.image_id;
        await axios.put(`${API}/templates/${templateId}/move-image`, {
          image_id: imageId,
          new_circle_index: slotIndex,
        });
      }
      
      alert("Image added");
      fetchTemplate();
      fetchImages();
    } catch (error) {
      if (error.response?.status === 400) {
        alert(error.response.data.detail || "Invalid image format. Use JPG, PNG, or HEIC.");
      } else if (error.response?.status === 507) {
        alert("Out of memory. Try a smaller image (under 5MB).");
      } else if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert("Failed to upload image");
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file && uploadingToSlot !== null) {
      await uploadImageToSlot(file, uploadingToSlot);
      setUploadingToSlot(null);
    }
    e.target.value = ""; // Reset input
  };

  const applyImageToAllSlots = async () => {
    if (!bulkUploadFile) {
      alert("Please select an image first");
      return;
    }

    const emptySlots = [];
    for (let i = 0; i < template.total_slots; i++) {
      if (!images.find(img => img.circle_index === i)) {
        emptySlots.push(i);
      }
    }

    if (emptySlots.length === 0) {
      alert("All slots are already filled. Delete some images first.");
      return;
    }

    if (!window.confirm(`Apply this image to ${emptySlots.length} empty slots?`)) {
      return;
    }

    setApplyingBulk(true);

    try {
      const formData = new FormData();
      formData.append("file", bulkUploadFile);
      
      const response = await axios.post(`${API}/templates/${templateId}/bulk-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      alert(`✅ ${response.data.filled_slots} slots filled in one go!`);
      
      setBulkUploadFile(null);
      
      // Clear loaded images cache and loading ref to force fresh load
      loadingImagesRef.current = new Set();
      setLoadedImages({});
      
      fetchTemplate();
      fetchImages();
    } catch (error) {
      console.error("Bulk upload error:", error);
      if (error.response?.status === 400) {
        alert(error.response.data.detail || "Invalid image format. Use JPG, PNG, or HEIC.");
      } else if (error.response?.status === 507) {
        alert("Out of memory. Try a smaller image (under 5MB).");
      } else if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert("Failed to apply to slots. Check image format (JPG, PNG, HEIC).");
      }
    } finally {
      setApplyingBulk(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}/pdf`, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${template.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert("PDF downloaded");
    } catch (error) {
      alert("Failed to download PDF");
    }
  };

  // Bulk upload multiple images to fill empty slots
  const handleBulkMultiUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setBulkMultiUploading(true);
    setBulkMultiProgress({ current: 0, total: files.length });

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setBulkMultiProgress({ current: i + 1, total: files.length });

      try {
        const formData = new FormData();
        formData.append("file", file);

        await axios.post(`${API}/templates/${templateId}/upload-owner`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    setBulkMultiUploading(false);
    setBulkMultiProgress({ current: 0, total: 0 });
    
    if (bulkMultiFileInputRef.current) {
      bulkMultiFileInputRef.current.value = '';
    }

    alert(`Bulk upload complete!\n✅ ${successCount} uploaded\n❌ ${failCount} failed`);
    
    // Clear loaded images cache and loading ref to force fresh load
    loadingImagesRef.current = new Set();
    setLoadedImages({});
    
    fetchTemplate();
    fetchImages();
  };

  if (!template) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/photo-booth")}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {template.name}
              </h1>
              <p className="text-gray-600">
                {template.template_type === "single" 
                  ? "Single Photo (4\"×6\")"
                  : template.template_type === "branded"
                  ? "Branded Card (2.25\"×3.25\")"
                  : template.template_type === "postcard"
                  ? "Postcard (3.5\"×6.5\")"
                  : template.template_type === "text-postcard"
                  ? "Text-Only Postcard (3.5\"×6.5\")"
                  : template.template_type === "rectangle" 
                  ? "Rectangle Template" 
                  : "Circle Template"
                }{template.template_type !== "text-postcard" && (
                  <> •{" "}{template.filled_count} / {template.total_slots} filled</>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            {selectedImage && template.template_type !== "text-postcard" && (
              <>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => rotateImage(90)}
                    data-testid="rotate-90-btn"
                    size="sm"
                  >
                    90°
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => rotateImage(180)}
                    data-testid="rotate-180-btn"
                    size="sm"
                  >
                    180°
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => rotateImage(270)}
                    data-testid="rotate-270-btn"
                    size="sm"
                  >
                    270°
                  </Button>
                </div>
                <Button
                  variant="destructive"
                  onClick={deleteImage}
                  data-testid="delete-selected-image-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              </>
            )}
            <Button
              onClick={downloadPDF}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
              data-testid="download-pdf-editor-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-2xl p-6 border-2 border-gray-200">
              <div className="mb-4">
                <h2 className="text-lg font-semibold mb-2">Template Preview</h2>
                <p className="text-sm text-gray-600">
                  Fixed borders show print positions. Click images to select, use controls to move/delete. High-quality print output.
                </p>
              </div>
              <div className="border-4 border-gray-300 rounded-lg overflow-auto bg-gray-100 p-4">
                <canvas
                  ref={canvasRef}
                  width={canvasSize.width}
                  height={canvasSize.height}
                  onClick={handleCanvasClick}
                  onMouseDown={handleDragStart}
                  onMouseMove={(e) => {
                    handleCanvasMouseMove(e);
                    handleDragMove(e);
                  }}
                  onMouseUp={handleDragEnd}
                  onMouseLeave={() => {
                    setHoveredSlot(null);
                    handleDragEnd();
                  }}
                  onTouchStart={handleDragStart}
                  onTouchMove={handleDragMove}
                  onTouchEnd={(e) => {
                    handleCanvasClick(e);
                    handleDragEnd();
                  }}
                  className={`bg-white shadow-lg mx-auto ${isDraggingImage ? 'cursor-grabbing' : 'cursor-pointer'}`}
                  data-testid="template-canvas"
                  style={{ maxWidth: '100%', height: 'auto', touchAction: 'none' }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="slot-file-input"
                />
                <input
                  ref={bulkMultiFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleBulkMultiUpload}
                  className="hidden"
                  data-testid="bulk-multi-file-input"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {template.template_type !== "text-postcard" && (
              <div className="bg-white rounded-xl shadow-xl p-6 border-2 border-gray-200">
                <h2 className="text-lg font-semibold mb-4">Bulk Upload</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Upload images to fill empty slots. Supports JPG, PNG, HEIC.
                </p>
                <div className="space-y-4">
                  {/* Option 1: Multiple images to multiple slots */}
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <h3 className="text-sm font-semibold text-purple-800 mb-2">📤 Upload Multiple Images</h3>
                    <p className="text-xs text-purple-600 mb-2">Select many images - each fills one empty slot</p>
                    <Button
                      onClick={() => bulkMultiFileInputRef.current?.click()}
                      disabled={bulkMultiUploading}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                      data-testid="bulk-multi-upload-btn"
                    >
                      {bulkMultiUploading ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Uploading {bulkMultiProgress.current}/{bulkMultiProgress.total}...
                        </div>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Select Multiple Images
                        </>
                      )}
                    </Button>
                    {bulkMultiUploading && (
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div
                          className="bg-purple-500 h-2 rounded-full transition-all"
                          style={{ width: `${(bulkMultiProgress.current / bulkMultiProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    )}
                  </div>

                  {/* Option 2: One image to all slots */}
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <h3 className="text-sm font-semibold text-green-800 mb-2">🔄 One Photo to All Slots</h3>
                    <p className="text-xs text-green-600 mb-2">Same image fills ALL empty slots</p>
                    <input
                      ref={bulkFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => setBulkUploadFile(e.target.files[0])}
                      className="w-full text-sm border rounded px-3 py-2 mb-2"
                      data-testid="bulk-upload-input"
                    />
                    {bulkUploadFile && (
                      <div className="text-sm text-gray-600 mb-2">
                        Selected: {bulkUploadFile.name}
                      </div>
                    )}
                    <Button
                      onClick={applyImageToAllSlots}
                      disabled={!bulkUploadFile || applyingBulk}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
                      data-testid="apply-to-all-btn"
                    >
                      {applyingBulk ? "Applying..." : "Apply to All Empty Slots"}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {template.template_type !== "text-postcard" && (
              <div className="bg-white rounded-xl shadow-xl p-6 border-2 border-gray-200">
                <h2 className="text-lg font-semibold mb-4">Selected Image</h2>
                {selectedImage ? (
                <div className="space-y-4">
                  {/* Live Preview with Adjustments */}
                  <div className={`aspect-square overflow-hidden border-2 border-purple-500 bg-gray-100 relative ${
                    template.template_type === 'circle' || template.template_type === 'circle-small' 
                      ? 'rounded-full' 
                      : 'rounded-lg'
                  }`}>
                    <canvas
                      ref={(canvas) => {
                        if (!canvas || !loadedImages[selectedImage.id]) return;
                        const ctx = canvas.getContext('2d');
                        const img = loadedImages[selectedImage.id];
                        if (!img) return;
                        
                        const size = 200;
                        canvas.width = size;
                        canvas.height = size;
                        
                        // Clear canvas
                        ctx.fillStyle = '#f3f4f6';
                        ctx.fillRect(0, 0, size, size);
                        
                        // Get adjustments
                        const adj = imageAdjustments[selectedImage.id] || { offset_x: 0, offset_y: 0, scale: 1, display_rotation: 0 };
                        
                        // Calculate scale to FIT image
                        const baseScale = Math.min(size / img.width, size / img.height);
                        const finalScale = baseScale * adj.scale;
                        
                        const imgW = img.width * finalScale;
                        const imgH = img.height * finalScale;
                        
                        // Center position with pan offsets
                        let imgX = (size - imgW) / 2 + (adj.offset_x / 100) * size;
                        let imgY = (size - imgH) / 2 + (adj.offset_y / 100) * size;
                        
                        // Apply rotation
                        ctx.save();
                        if (adj.display_rotation !== 0) {
                          ctx.translate(size / 2, size / 2);
                          ctx.rotate((adj.display_rotation * Math.PI) / 180);
                          ctx.translate(-size / 2, -size / 2);
                        }
                        
                        // Draw circular clip for circle templates
                        if (template.template_type === 'circle' || template.template_type === 'circle-small') {
                          ctx.beginPath();
                          ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
                          ctx.clip();
                        }
                        
                        ctx.drawImage(img, imgX, imgY, imgW, imgH);
                        ctx.restore();
                      }}
                      className="w-full h-full"
                      data-testid="selected-image-preview"
                    />
                    <div className="absolute bottom-1 right-1 bg-black/50 text-white text-xs px-2 py-1 rounded">
                      {((imageAdjustments[selectedImage.id]?.scale || 1) * 100).toFixed(0)}%
                    </div>
                  </div>
                  
                  {/* User Info Display */}
                  {(selectedImage.user_name || selectedImage.user_contact) && (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <h3 className="text-sm font-semibold text-green-800 mb-2">Uploaded By</h3>
                      {selectedImage.user_name && (
                        <p className="text-sm text-green-700" data-testid="selected-image-user-name">
                          <span className="font-medium">Name:</span> {selectedImage.user_name}
                        </p>
                      )}
                      {selectedImage.user_contact && (
                        <p className="text-sm text-green-700" data-testid="selected-image-user-contact">
                          <span className="font-medium">Contact:</span> {selectedImage.user_contact}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Image Adjustment Controls */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Move className="w-4 h-4" />
                      Adjust Image in Slot
                    </h3>
                    
                    {/* Zoom Control */}
                    <div className="mb-4">
                      <label className="text-xs font-medium text-blue-700 mb-1 flex items-center justify-between">
                        <span className="flex items-center gap-1">
                          <ZoomIn className="w-3 h-3" /> Zoom
                        </span>
                        <span>{((imageAdjustments[selectedImage.id]?.scale || 1) * 100).toFixed(0)}%</span>
                      </label>
                      <input
                        type="range"
                        min="50"
                        max="300"
                        value={(imageAdjustments[selectedImage.id]?.scale || 1) * 100}
                        onChange={(e) => updateImageAdjustment('scale', parseInt(e.target.value) / 100)}
                        className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                        data-testid="zoom-slider"
                      />
                    </div>
                    
                    {/* Pan X Control */}
                    <div className="mb-4">
                      <label className="text-xs font-medium text-blue-700 mb-1 flex items-center justify-between">
                        <span>Pan Horizontal</span>
                        <span>{(imageAdjustments[selectedImage.id]?.offset_x || 0).toFixed(0)}%</span>
                      </label>
                      <input
                        type="range"
                        min="-100"
                        max="100"
                        value={imageAdjustments[selectedImage.id]?.offset_x || 0}
                        onChange={(e) => updateImageAdjustment('offset_x', parseInt(e.target.value))}
                        className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                        data-testid="pan-x-slider"
                      />
                    </div>
                    
                    {/* Pan Y Control */}
                    <div className="mb-4">
                      <label className="text-xs font-medium text-blue-700 mb-1 flex items-center justify-between">
                        <span>Pan Vertical</span>
                        <span>{(imageAdjustments[selectedImage.id]?.offset_y || 0).toFixed(0)}%</span>
                      </label>
                      <input
                        type="range"
                        min="-100"
                        max="100"
                        value={imageAdjustments[selectedImage.id]?.offset_y || 0}
                        onChange={(e) => updateImageAdjustment('offset_y', parseInt(e.target.value))}
                        className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                        data-testid="pan-y-slider"
                      />
                    </div>
                    
                    {/* Display Rotation */}
                    <div className="mb-4">
                      <label className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                        <RotateCw className="w-3 h-3" /> Display Rotation
                      </label>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => rotateDisplayImage('ccw')}
                          className="flex-1"
                          data-testid="rotate-ccw-btn"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        <span className="flex items-center justify-center w-16 text-sm font-medium">
                          {imageAdjustments[selectedImage.id]?.display_rotation || 0}°
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => rotateDisplayImage('cw')}
                          className="flex-1"
                          data-testid="rotate-cw-btn"
                        >
                          <RotateCw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Save/Reset Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={saveImageAdjustment}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        size="sm"
                        data-testid="save-adjustment-btn"
                      >
                        Save Adjustment
                      </Button>
                      <Button
                        variant="outline"
                        onClick={resetImageAdjustment}
                        size="sm"
                        data-testid="reset-adjustment-btn"
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Current position: Slot #{selectedImage.circle_index + 1}
                    </p>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Move to slot:</label>
                      <select
                        className="w-full border rounded px-3 py-2"
                        value={selectedImage.circle_index}
                        onChange={(e) => moveImageToSlot(parseInt(e.target.value))}
                        data-testid="move-image-select"
                      >
                        {Array.from({ length: template.total_slots }, (_, i) => (
                          <option key={i} value={i}>
                            Slot #{i + 1}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Rotate source image:</label>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <Button
                          variant="outline"
                          onClick={() => rotateImage(90)}
                          data-testid="rotate-90-sidebar-btn"
                          size="sm"
                        >
                          90°
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => rotateImage(180)}
                          data-testid="rotate-180-sidebar-btn"
                          size="sm"
                        >
                          180°
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => rotateImage(270)}
                          data-testid="rotate-270-sidebar-btn"
                          size="sm"
                        >
                          270°
                        </Button>
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={deleteImage}
                      className="w-full"
                      data-testid="delete-image-sidebar-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Image
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <ImageIcon className="w-16 h-16 mx-auto mb-3 opacity-50" />
                  <p>Click an image to select</p>
                </div>
              )}
              </div>
            )}

            {template.template_type !== "text-postcard" && (
              <div className="bg-white rounded-xl shadow-xl p-6 border-2 border-gray-200">
                <h2 className="text-lg font-semibold mb-4">All Images ({images.length})</h2>
              <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                {images.map((img, index) => (
                  <div
                    key={img.id}
                    className={`relative group rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                      selectedImage?.id === img.id
                        ? "border-purple-500 ring-2 ring-purple-300"
                        : "border-gray-200 hover:border-purple-300"
                    }`}
                    data-testid={`thumbnail-${img.id}`}
                  >
                    <div 
                      className="aspect-square"
                      onClick={() => setSelectedImage(img)}
                    >
                      <img
                        src={`${API}/images/${img.id}`}
                        alt={`Slot ${img.circle_index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* User info and download button */}
                    <div className="p-2 bg-gray-50 border-t">
                      <p className="text-xs font-medium text-gray-700 truncate" title={img.user_name}>
                        {img.user_name || `Photo ${index + 1}`}
                      </p>
                      {img.user_contact && (
                        <p className="text-xs text-gray-500 truncate" title={img.user_contact}>
                          {img.user_contact}
                        </p>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadImagePdf(img.id, img.sequence_number || index + 1);
                        }}
                        className="w-full mt-2 text-xs h-7"
                        data-testid={`download-pdf-${img.id}`}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        {img.sequence_number || index + 1}.pdf
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {images.length === 0 && (
                <p className="text-center text-gray-400 py-8">No images yet</p>
              )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const ImageIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);
